import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPatterns();
  }, [user]);

  const fetchPatterns = async () => {
    try {
      const headers = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
      
      const response = await axios.get(`${API}/patterns`, { headers });
      setPatterns(response.data);
    } catch (error) {
      console.error('Error fetching patterns:', error);
      toast.error('Error al cargar patrones');
    } finally {
      setLoading(false);
    }
  };

  const startSession = (patternId) => {
    navigate(`/session/${patternId}`);
  };

  // Función para obtener el nombre corto del patrón
  const getShortName = (name) => {
    if (name === "Pre-Entrenamiento") return "Pre-Entrena";
    if (name.startsWith("BreathFire")) return `BF ${name.split(" ")[1]}`;
    if (name.includes("Meditación")) return "Medita";
    return name;
  };

  // Función para obtener el estilo de borde animado según el patrón
  const getBorderStyle = (name) => {
    let color1, color2;
    
    if (name === "Pre-Entrenamiento") {
      color1 = "rgba(168, 85, 247, 0.8)"; // purple-500
      color2 = "rgba(236, 72, 153, 0.8)"; // pink-500
    } else if (name === "BreathFire 1") {
      color1 = "rgba(103, 232, 249, 0.6)"; // cyan-300
      color2 = "rgba(34, 211, 238, 0.6)"; // cyan-400
    } else if (name === "BreathFire 2") {
      color1 = "rgba(34, 211, 238, 0.7)"; // cyan-400
      color2 = "rgba(6, 182, 212, 0.7)"; // cyan-500
    } else if (name === "BreathFire 3") {
      color1 = "rgba(6, 182, 212, 0.8)"; // cyan-500
      color2 = "rgba(8, 145, 178, 0.8)"; // cyan-600
    } else if (name === "BreathFire 4") {
      color1 = "rgba(8, 145, 178, 0.9)"; // cyan-600
      color2 = "rgba(14, 116, 144, 0.9)"; // cyan-700
    } else if (name.includes("Meditación")) {
      color1 = "rgba(251, 191, 36, 0.8)"; // amber-400
      color2 = "rgba(249, 115, 22, 0.8)"; // orange-500
    } else {
      color1 = "rgba(156, 163, 175, 0.6)"; // gray-400
      color2 = "rgba(107, 114, 128, 0.6)"; // gray-500
    }
    
    return {
      background: `conic-gradient(
        from 0deg,
        transparent 0deg,
        transparent 50deg,
        ${color1} 90deg,
        ${color2} 150deg,
        ${color1} 210deg,
        transparent 270deg,
        transparent 360deg
      )`
    };
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8" data-testid="home-page">
      {/* Hero Section */}
      <div className="text-center mb-8 sm:mb-12">
        {/* Logo Principal - Modo Claro/Oscuro */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img 
            src="https://academiacortex.com.ar/imagenes/logoP2negro.png" 
            alt="Academia Cortex" 
            className="h-16 sm:h-20 lg:h-24 xl:h-28 w-auto object-contain dark:hidden"
          />
          <img 
            src="https://academiacortex.com.ar/imagenes/logoBlanco.png" 
            alt="Academia Cortex" 
            className="h-16 sm:h-20 lg:h-24 xl:h-28 w-auto object-contain hidden dark:block"
          />
        </div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-3 sm:mb-4 text-[#77C9D4]">
          Respiración Consciente
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2">
          Despertando capacidades mentales dormidas con técnicas ancestrales.
        </p>
      </div>

      {/* Patterns Grid - Compact Cards with Animated Border */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 max-w-5xl mx-auto">
          {patterns.map((pattern) => (
            <div
              key={pattern.id}
              className="relative group cursor-pointer"
              onClick={() => startSession(pattern.id)}
              data-testid={`pattern-card-${pattern.id}`}
            >
              {/* Animated border wrapper */}
              <div className="absolute -inset-[2px] rounded-xl overflow-hidden">
                <div 
                  className="absolute -inset-[100%] animate-spin-border"
                  style={getBorderStyle(pattern.name)}
                />
              </div>
              
              {/* Card content */}
              <div className="relative bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 transition-all duration-300 hover:scale-105 shadow-lg">
                <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3">
                  {/* Ícono con soporte para modo oscuro */}
                  <img 
                    src="https://academiacortex.com.ar/imagenes/iconNegro.png" 
                    alt="Cortex" 
                    className="w-8 h-8 object-contain dark:hidden" 
                  />
                  <img 
                    src="https://academiacortex.com.ar/imagenes/iconBlanco.png" 
                    alt="Cortex" 
                    className="w-8 h-8 object-contain hidden dark:block" 
                  />
                  <h3 className="text-sm sm:text-base font-bold text-[#2C3E50] dark:text-white text-center leading-tight">
                    {getShortName(pattern.name)}
                  </h3>
                  <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 text-center space-y-0.5">
                    <div>{pattern.breaths_count} resp</div>
                    <div>{pattern.rounds} ronda{pattern.rounds > 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {patterns.length === 0 && !loading && (
        <div className="text-center py-20">
          <img 
            src="https://academiacortex.com.ar/imagenes/iconNegro.png" 
            alt="Cortex" 
            className="w-16 h-16 object-contain mx-auto mb-4 opacity-30 dark:hidden" 
          />
          <img 
            src="https://academiacortex.com.ar/imagenes/iconBlanco.png" 
            alt="Cortex" 
            className="w-16 h-16 object-contain mx-auto mb-4 opacity-30 hidden dark:block" 
          />
          <p className="text-gray-500 dark:text-gray-400 text-lg">No hay patrones disponibles</p>
        </div>
      )}

      {/* Animated border keyframes - Added via inline style */}
      <style jsx>{`
        @keyframes spin-border {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .animate-spin-border {
          animation: spin-border 3s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Home;